import { useEffect, useState } from 'react';

import { formatISO, isValid } from 'date-fns';
import PropTypes from 'prop-types';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Box,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';

import { useMuiPickerLocale, useTransl } from 'shared/lib/i18n';

import StyledTimePicker from '../TimePicker';

const TimePickerComponent = (props) => {
  const { t } = useTransl();
  const { localeText, adapterLocale } = useMuiPickerLocale();
  const {
    input: { value: formValue, onChange, name },
    meta: { error, touched },
    disabled,
    disablePast, // mobile picker only
    getDisabledHours,
    getDisabledMinutes,
    timeFormat = 'HH:mm',
    use12Hours = false,
    minuteStep = 15,
    isMobilePicker,
    isClearable = true,
  } = props;

  const [value, setValue] = useState(formValue);
  const onClose = () => onChange(value, name);

  useEffect(() => {
    setValue(formValue);
  }, [formValue]);

  const onDateChange = (date) => {
    if (!date) return onChange(null);
    if (!isValid(date)) return 'invalid';

    const newDate = formatISO(date);

    return onChange(newDate);
  };

  const hasError = touched && !!error;

  const textField = (params) => (
    <TextField
      size="small"
      error={hasError}
      {...params}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <AccessTimeIcon />
          </InputAdornment>
        ),
        endAdornment:
          value && isClearable ? (
            <InputAdornment position="end">
              <IconButton
                aria-label={t('ariaLabel.clear')}
                onClick={(event) => {
                  event.stopPropagation();
                  onDateChange(null);
                }}
                edge="end"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
      }}
    />
  );

  return (
    <Box>
      {isMobilePicker ? (
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          localeText={localeText}
          adapterLocale={adapterLocale}
        >
          <MobileTimePicker
            value={value ? new Date(value) : null}
            onChange={onDateChange}
            slots={{ textField }}
            disabled={disabled}
            disablePast={disablePast}
          />
        </LocalizationProvider>
      ) : (
        <StyledTimePicker
          onChange={(newValue) => setValue(newValue)}
          onClose={onClose}
          value={value || null}
          disabled={disabled}
          allowEmpty={false}
          showSecond={false}
          disabledHours={() => getDisabledHours && getDisabledHours()}
          disabledMinutes={(selectedHour) =>
            getDisabledMinutes && getDisabledMinutes(selectedHour)
          }
          format={timeFormat}
          showNow={false}
          minuteStep={minuteStep}
          use12Hours={use12Hours}
          style={{ height: '4rem', width: '18rem' }}
          inputIcon={
            <InputAdornment
              position="end"
              style={{ color: disabled ? '#00000061' : '#666' }}
            >
              <AccessTimeIcon color="inherit" />
            </InputAdornment>
          }
        />
      )}
      {hasError && (
        <Box mx="1.4rem">
          <FormHelperText error>{error}</FormHelperText>
        </Box>
      )}
    </Box>
  );
};
TimePickerComponent.defaultProps = {
  use12Hours: false,
  minuteStep: 15,
  timeFormat: 'HH:mm',
  getDisabledMinutes: null,
};
TimePickerComponent.propTypes = {
  input: PropTypes.instanceOf(Object).isRequired,
  meta: PropTypes.instanceOf(Object).isRequired,
  disabled: PropTypes.bool,
  disablePast: PropTypes.bool,
  getDisabledHours: PropTypes.func,
  getDisabledMinutes: PropTypes.func,
  timeFormat: PropTypes.string,
  use12Hours: PropTypes.bool,
  minuteStep: PropTypes.number,
  isMobilePicker: PropTypes.bool,
  isClearable: PropTypes.bool,
};

export default TimePickerComponent;
